import { useState } from 'react';
import {
    Box, Card, CardContent, Grid, Typography, Tabs, Tab,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Chip, Alert, ToggleButton, ToggleButtonGroup, Stack, IconButton, Button,
    CircularProgress, Dialog, DialogTitle, DialogContent, Select, MenuItem,
    FormControlLabel, Switch,
} from '@mui/material';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, ReferenceLine } from 'recharts';
import { useQuery } from 'react-query';
import { supabase } from '../lib/supabaseClient';
import { Loading } from 'react-admin';
import {
    buildFunnelRows, channelLabel, dailyCumulative, normalizeLang, passesPageFilters, wonTotalCumulative,
} from './funnelSummary';
import type { FunnelDailyRow, FunnelSegment, PageFilters, SourceFilter, WonTotalRow } from './funnelSummary';

interface MonthlyRecapProps {
    selectedMonth: string; // format: 'YYYY-MM'
    onBack: () => void;
}

const Delta = ({ current, previous }: { current: number; previous: number | null | undefined }) => {
    if (previous == null || previous === 0) return null;
    const pct = Math.round(((current - previous) / previous) * 100);
    if (pct === 0) return null;
    const color = pct > 0 ? '#2e7d32' : '#c62828';
    const arrow = pct > 0 ? '\u25B2' : '\u25BC';
    return (
        <Typography component="span" variant="caption" sx={{ color, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {arrow} {Math.abs(pct)}%
        </Typography>
    );
};

const ValueWithDelta = ({ value, prev, bold }: { value: number; prev?: number | null; bold?: boolean }) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5, minWidth: 80, justifyContent: 'flex-end' }}>
        <Typography component="span" sx={{ minWidth: 30, textAlign: 'right', ...(bold ? { fontWeight: 700 } : {}) }}>{value}</Typography>
        <Box sx={{ minWidth: 45 }}>
            {prev != null ? <Delta current={value} previous={prev} /> : null}
        </Box>
    </Box>
);

// Like ValueWithDelta but for a rate: renders "NN%" (or "-") with the same
// month-over-month delta. The delta is the relative change of the rate, matching
// every other delta on the page.
const RateWithDelta = ({ rate, prevRate, bold }: { rate: number | null; prevRate?: number | null; bold?: boolean }) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5, minWidth: 70, justifyContent: 'flex-end' }}>
        <Typography component="span" sx={{ minWidth: 30, textAlign: 'right', ...(bold ? { fontWeight: 700 } : {}) }}>
            {rate != null ? `${rate.toFixed(0)}%` : '-'}
        </Typography>
        <Box sx={{ minWidth: 45 }}>
            {rate != null && prevRate != null ? <Delta current={rate} previous={prevRate} /> : null}
        </Box>
    </Box>
);

const formatMonthLabel = (month: string) => {
    const date = new Date(`${month}-01T00:00:00`);
    return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
};


export const MonthlyRecap = ({ selectedMonth, onBack }: MonthlyRecapProps) => {
    // Page-wide filter, pinned to the top on scroll. Values match quotes.timeline.
    // Scopes every lead-funnel metric (Qualified/Pending/Won/Lost/Won Total).
    // Call/form-volume metrics have no timeline and stay full-month.
    const [timelineFilter, setTimelineFilter] = useState<string>('all');
    // Page-wide source-channel filter, sibling to the timeline filter. Values
    // match leads.source_channel ('organic' / 'ad'; 'maps' = both maps_gbp_*);
    // 'all' matches every channel.
    const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');

    // Same trigger React-Admin's AppBar uses for HideOnScroll, so the sticky
    // filter tracks the header: dropped just below it when visible, flush to the
    // top when the AppBar slides away on scroll-down.
    const headerHidden = useScrollTrigger();

    const [lostCases, setLostCases] = useState<any[]>([]);
    const [aiAnalyses, setAiAnalyses] = useState<Record<string, string>>({});
    const [loadingRecap, setLoadingRecap] = useState(false);
    const [replacingIndex, setReplacingIndex] = useState<number | null>(null);
    const [chartRow, setChartRow] = useState<{ label: string; segment: FunnelSegment } | null>(null);
    // Adds the Won series to the unfiltered chart. Off by default, and reset
    // each time a row is opened. The filtered chart always shows all three.
    const [showWon, setShowWon] = useState(false);
    const [execSummary, setExecSummary] = useState<string>('');
    const [loadingExecSummary, setLoadingExecSummary] = useState(false);

    // When viewing the current (partial) month, clamp both the selected-month and
    // previous-month windows to the same day-of-month so comparisons are apples-to-apples.
    const [selYear, selMonthNum] = selectedMonth.split('-').map(Number);
    const pad = (n: number) => String(n).padStart(2, '0');
    const fmtDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const now = new Date();
    const isCurrentMonth = now.getFullYear() === selYear && now.getMonth() + 1 === selMonthNum;
    const todayDay = now.getDate();

    const startDate = `${selYear}-${pad(selMonthNum)}-01`;
    const fullMonthEnd = selMonthNum === 12
        ? `${selYear + 1}-01-01`
        : `${selYear}-${pad(selMonthNum + 1)}-01`;
    // Exclusive end: day after today when current month, else first of next month.
    const endDate = isCurrentMonth
        ? fmtDate(new Date(selYear, selMonthNum - 1, todayDay + 1))
        : fullMonthEnd;

    const prevMonthNum = selMonthNum === 1 ? 12 : selMonthNum - 1;
    const prevYear = selMonthNum === 1 ? selYear - 1 : selYear;
    const prevStartDate = `${prevYear}-${pad(prevMonthNum)}-01`;
    const prevFullMonthEnd = startDate; // current month start = prev month end
    // Match the day-of-month cap in the previous month, clipped to its length.
    const prevMonthLastDay = new Date(selYear, selMonthNum - 1, 0).getDate();
    const prevEndDate = isCurrentMonth
        ? fmtDate(new Date(prevYear, prevMonthNum - 1, Math.min(todayDay + 1, prevMonthLastDay + 1)))
        : prevFullMonthEnd;

    // Comparison month for the daily-breakdown chart. Defaults to the previous
    // month; the user can pick any of the 12 months before the selected month
    // from the dialog header. Table deltas always compare to the previous month.
    const defaultCompareMonth = `${prevYear}-${pad(prevMonthNum)}`;
    const [compareMonthOverride, setCompareMonthOverride] = useState<string | null>(null);
    const compareMonth = compareMonthOverride ?? defaultCompareMonth;
    const [cmpYear, cmpMonthNum] = compareMonth.split('-').map(Number);
    const compareStartDate = `${cmpYear}-${pad(cmpMonthNum)}-01`;
    const compareEndDate = cmpMonthNum === 12
        ? `${cmpYear + 1}-01-01`
        : `${cmpYear}-${pad(cmpMonthNum + 1)}-01`;
    const compareDaysInMonth = new Date(cmpYear, cmpMonthNum, 0).getDate();
    const compareMonthOptions = Array.from({ length: 12 }, (_, i) => {
        const d = new Date(selYear, selMonthNum - 2 - i, 1);
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
    });

    // Call each RPC individually — same as the dashboard does
    const { data: recapData, isLoading: isLoadingRecap } = useQuery(
        ['monthlyRecapMetrics', selectedMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_monthly_recap', {
                p_start: startDate,
                p_end: endDate,
            });
            if (error) throw error;
            if (typeof data === 'string') return JSON.parse(data);
            if (Array.isArray(data)) return data[0];
            return data;
        }
    );

    const { isLoading: isLoadingAgents } = useQuery(
        ['monthlyRecapAgents', selectedMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_agent_breakdown', {
                p_start: startDate,
                p_end: endDate,
            });
            if (error) throw error;
            return data;
        }
    );

    const { data: agentPendingWonTotal } = useQuery(
        ['monthlyRecapAgentPendingWonTotal', selectedMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_agent_pending_won_total', {
                p_start: startDate,
                p_end: endDate,
            });
            if (error) throw error;
            return data as { agent_name: string; deal_type: string; region: string; language: string; source_channel: string; timeline: string | null; pending: number; won_total: number }[] | null;
        }
    );

    // Timeline-aware agent qualified/won/lost (replaces recap.agent_detail so the
    // agent perf tables can be scoped by the global timeline filter).
    const { data: agentDetailByTimeline } = useQuery(
        ['monthlyRecapAgentDetailByTimeline', selectedMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_agent_detail_by_timeline', {
                p_start: startDate,
                p_end: endDate,
            });
            if (error) throw error;
            return data as { agent_name: string; deal_type: string; region: string; language: string; source_channel: string; timeline: string | null; qualified: number; won: number; lost: number }[] | null;
        }
    );

    // Previous-month agent data — powers the month-over-month deltas in the Agent
    // Performance tables (same windows as the other prev-month queries).
    const { data: prevAgentDetailByTimeline } = useQuery(
        ['monthlyRecapPrevAgentDetailByTimeline', selectedMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_agent_detail_by_timeline', {
                p_start: prevStartDate,
                p_end: prevEndDate,
            });
            if (error) throw error;
            return data as { agent_name: string; deal_type: string; region: string; language: string; source_channel: string; timeline: string | null; qualified: number; won: number; lost: number }[] | null;
        }
    );

    const { data: prevAgentPendingWonTotal } = useQuery(
        ['monthlyRecapPrevAgentPendingWonTotal', selectedMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_agent_pending_won_total', {
                p_start: prevStartDate,
                p_end: prevEndDate,
            });
            if (error) throw error;
            return data as { agent_name: string; deal_type: string; region: string; language: string; source_channel: string; timeline: string | null; pending: number; won_total: number }[] | null;
        }
    );

    // Timeline-aware weekly qualified/won/pending for the Weekly Trends table.
    const { data: weeklyFunnelByTimeline } = useQuery(
        ['monthlyRecapWeeklyFunnelByTimeline', selectedMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_weekly_funnel_by_timeline', {
                p_start: startDate,
                p_end: endDate,
            });
            if (error) throw error;
            return data as { week_start: string; source_channel: string; timeline: string | null; qualified: number; won: number; pending: number }[] | null;
        }
    );

    // Timeline-aware day-of-week qualified for the Day of Week table.
    const { data: dayOfWeekQualByTimeline } = useQuery(
        ['monthlyRecapDayOfWeekQualByTimeline', selectedMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_day_of_week_qualified_by_timeline', {
                p_start: startDate,
                p_end: endDate,
            });
            if (error) throw error;
            return data as { isodow: number; source_channel: string; timeline: string | null; qualified: number }[] | null;
        }
    );

    const { data: languageData, isLoading: isLoadingLanguages } = useQuery(
        ['monthlyRecapLanguages', selectedMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_language_breakdown', {
                p_start: startDate,
                p_end: endDate,
            });
            if (error) throw error;
            return data;
        }
    );

    const { data: qualifiedByRegion } = useQuery(
        ['monthlyRecapQualByRegion', selectedMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_qualified_by_region', {
                p_start: startDate,
                p_end: endDate,
            });
            if (error) throw error;
            return data;
        }
    );

    const { data: wonByRegion } = useQuery(
        ['monthlyRecapWonByRegion', selectedMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_won_by_region', {
                p_start: startDate,
                p_end: endDate,
            });
            if (error) throw error;
            return data;
        }
    );

    const { data: pendingByRegion } = useQuery(
        ['monthlyRecapPendingByRegion', selectedMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_form_filled_by_region', {
                p_start: startDate,
                p_end: endDate,
            });
            if (error) throw error;
            return data as { region: string; deal_type: string; language: string; source_channel: string; timeline: string | null; pending: number }[] | null;
        }
    );

    const { data: prevPendingByRegion } = useQuery(
        ['monthlyRecapPrevPendingByRegion', selectedMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_form_filled_by_region', {
                p_start: prevStartDate,
                p_end: prevEndDate,
            });
            if (error) throw error;
            return data as { region: string; deal_type: string; language: string; source_channel: string; timeline: string | null; pending: number }[] | null;
        }
    );

    const { data: leadSourceData } = useQuery(
        ['monthlyRecapLeadSource', selectedMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_lead_source_breakdown', {
                p_start: startDate,
                p_end: endDate,
            });
            if (error) throw error;
            return data as { source_channel: string; timeline: string | null; qualified: number; pending: number; won: number; lost: number }[] | null;
        }
    );

    const { data: prevLeadSourceData } = useQuery(
        ['monthlyRecapPrevLeadSource', selectedMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_lead_source_breakdown', {
                p_start: prevStartDate,
                p_end: prevEndDate,
            });
            if (error) throw error;
            return data as { source_channel: string; timeline: string | null; qualified: number; pending: number; won: number; lost: number }[] | null;
        }
    );

    // Previous month data (for chart + comparison deltas)
    const { data: prevMonthRecap } = useQuery(
        ['monthlyRecapPrev', selectedMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_monthly_recap', {
                p_start: prevStartDate,
                p_end: prevEndDate,
            });
            if (error) throw error;
            if (typeof data === 'string') return JSON.parse(data);
            if (Array.isArray(data)) return data[0];
            return data;
        }
    );

    const { data: prevQualifiedByRegion } = useQuery(
        ['monthlyRecapPrevQualByRegion', selectedMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_qualified_by_region', { p_start: prevStartDate, p_end: prevEndDate });
            if (error) throw error;
            return data;
        }
    );

    const { data: prevWonByRegion } = useQuery(
        ['monthlyRecapPrevWonByRegion', selectedMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_won_by_region', { p_start: prevStartDate, p_end: prevEndDate });
            if (error) throw error;
            return data;
        }
    );

    // Timeline-aware sources for the Funnel Summary's timeline filter. Lost +
    // language rows can't read quotes.timeline from get_monthly_recap, so they
    // come from dedicated RPCs that mirror the recap subqueries plus a timeline
    // column. Summed across buckets these equal the recap totals (verified: no
    // lead carries >1 distinct timeline), so the "All" view is unchanged.
    const { data: lostByRegion } = useQuery(
        ['monthlyRecapLostByRegion', selectedMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_lost_by_region', { p_start: startDate, p_end: endDate });
            if (error) throw error;
            return data as { deal_type: string; region: string; source_channel: string; timeline: string | null; count: number }[] | null;
        }
    );

    type FunnelLangRow = { deal_type: string; region: string; language: string; timeline: string | null; qualified: number; won: number; lost: number };
    const { data: funnelByLanguage } = useQuery(
        ['monthlyRecapFunnelByLang', selectedMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_funnel_by_language', { p_start: startDate, p_end: endDate });
            if (error) throw error;
            return data as FunnelLangRow[] | null;
        }
    );

    const { data: prevFunnelByLanguage } = useQuery(
        ['monthlyRecapPrevFunnelByLang', selectedMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_funnel_by_language', { p_start: prevStartDate, p_end: prevEndDate });
            if (error) throw error;
            return data as FunnelLangRow[] | null;
        }
    );

    // Daily Qualified/Won for the row chart: the Funnel Summary's own counts
    // split by day. Same window as the table, so a row's line ends on the row's
    // number. Fetched once a row is opened.
    const { data: funnelDaily, isError: funnelDailyFailed } = useQuery(
        ['monthlyRecapFunnelDaily', selectedMonth, endDate],
        async () => {
            const { data, error } = await supabase.rpc('get_funnel_daily', { p_start: startDate, p_end: endDate });
            if (error) throw error;
            return (data || []) as FunnelDailyRow[];
        },
        { enabled: chartRow !== null }
    );

    // Comparison-month series for the row chart (full compare month). Separate
    // from the prev-month queries so the chart can compare against any
    // user-selected month without touching the table deltas.
    const { data: compareFunnelDaily } = useQuery(
        ['monthlyRecapCompareFunnelDaily', compareMonth],
        async () => {
            const { data, error } = await supabase.rpc('get_funnel_daily', { p_start: compareStartDate, p_end: compareEndDate });
            if (error) throw error;
            return (data || []) as FunnelDailyRow[];
        },
        { enabled: chartRow !== null }
    );

    // Funerals signed this month (no attribution) — powers the "Won Total"
    // column in the table and the "Won Total" line in the chart. Always fetch
    // the full month so the chart can render every day; the column aggregate
    // filters the previous-month rows to day-of-month client-side when viewing
    // the current (partial) month.
    const { data: wonTotalRows } = useQuery(
        ['monthlyRecapWonTotal', selectedMonth],
        async () => {
            const { data } = await supabase
                .from('funerals')
                .select('contracts_signed_at,region,has_preneed,has_atneed,language,quotes(timeline,leads(source_channel))')
                .not('contracts_signed_at', 'is', null)
                .gte('contracts_signed_at', startDate)
                .lt('contracts_signed_at', fullMonthEnd);
            return (data || []) as WonTotalRow[];
        }
    );

    const { data: prevWonTotalRows } = useQuery(
        ['monthlyRecapPrevWonTotal', selectedMonth],
        async () => {
            const { data } = await supabase
                .from('funerals')
                .select('contracts_signed_at,region,has_preneed,has_atneed,language,quotes(timeline,leads(source_channel))')
                .not('contracts_signed_at', 'is', null)
                .gte('contracts_signed_at', prevStartDate)
                .lt('contracts_signed_at', prevFullMonthEnd);
            return (data || []) as WonTotalRow[];
        }
    );

    const { data: compareWonTotalRows } = useQuery(
        ['monthlyRecapCompareWonTotal', compareMonth],
        async () => {
            const { data } = await supabase
                .from('funerals')
                .select('contracts_signed_at,region,has_preneed,has_atneed,language,quotes(timeline,leads(source_channel))')
                .not('contracts_signed_at', 'is', null)
                .gte('contracts_signed_at', compareStartDate)
                .lt('contracts_signed_at', compareEndDate);
            return (data || []) as WonTotalRow[];
        }
    );

    const isLoading = isLoadingRecap || isLoadingAgents || isLoadingLanguages;

    // Page-wide Timeline + Source predicate, shared with the Funnel Summary rows
    // and their chart (see passesPageFilters).
    const pageFilters: PageFilters = { timeline: timelineFilter, source: sourceFilter };
    const rowOk = (r: any) => passesPageFilters(r, pageFilters);

    const handleGenerateExecSummary = async (metricsPayload: Record<string, unknown>) => {
        setLoadingExecSummary(true);
        try {
            const session = await supabase.auth.getSession();
            const resp = await fetch(
                `${(process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001').replace(/\/$/, '')}/analytics/exec-summary`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${session.data.session?.access_token}`,
                    },
                    body: JSON.stringify({
                        month_label: formatMonthLabel(selectedMonth),
                        metrics: metricsPayload,
                    }),
                }
            );
            const result = await resp.json();
            setExecSummary(result?.summary || '');
        } catch (err) {
            console.error('Failed to generate exec summary:', err);
        } finally {
            setLoadingExecSummary(false);
        }
    };

    const fetchAiAnalysis = async (cases: any[]) => {
        try {
            const session = await supabase.auth.getSession();
            const resp = await fetch(
                `${(process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001').replace(/\/$/, '')}/analytics/lost-case-analysis`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${session.data.session?.access_token}`,
                    },
                    body: JSON.stringify({ cases }),
                }
            );
            const result = await resp.json();
            return result;
        } catch {
            return {};
        }
    };

    const handleGenerateRecap = async () => {
        setLoadingRecap(true);
        try {
            const { data, error } = await supabase.rpc('get_lost_case_details', {
                p_start: startDate,
                p_end: endDate,
                p_limit: 5,
            });
            if (error) throw error;
            setLostCases(data || []);

            const response = await fetchAiAnalysis(data || []);
            const mapped: Record<string, string> = {};
            const analyses = response?.analyses || response;
            if (Array.isArray(analyses)) {
                analyses.forEach((a: any) => {
                    if (a.lead_id) mapped[a.lead_id] = a.analysis;
                });
            }
            setAiAnalyses(mapped);
        } catch (err) {
            console.error('Failed to generate recap:', err);
        } finally {
            setLoadingRecap(false);
        }
    };

    const handleReplaceCase = async (index: number) => {
        setReplacingIndex(index);
        const currentLeadIds = lostCases.map((c: any) => c.lead_id);
        try {
            const { data: rawData, error } = await supabase.rpc('get_lost_case_details', {
                p_start: startDate,
                p_end: endDate,
                p_limit: 1,
                p_exclude_ids: currentLeadIds,
            });
            if (error) throw error;
            const cases = typeof rawData === 'string' ? JSON.parse(rawData) : Array.isArray(rawData) ? rawData : [];
            if (cases.length > 0) {
                const newCase = cases[0];
                const response = await fetchAiAnalysis([newCase]);
                const mapped: Record<string, string> = {};
                const analyses = response?.analyses || response;
                if (Array.isArray(analyses)) {
                    analyses.forEach((a: any) => {
                        if (a.lead_id) mapped[a.lead_id] = a.analysis;
                    });
                }

                setLostCases((prev) => {
                    const updated = [...prev];
                    updated[index] = newCase;
                    return updated;
                });
                setAiAnalyses((prev) => ({ ...prev, ...mapped }));
            }
        } catch (err) {
            console.error('Failed to replace case:', err);
        } finally {
            setReplacingIndex(null);
        }
    };


    if (isLoading) return <Loading />;

    const callMetrics = recapData?.call_metrics || {};
    const weeklyTrends = recapData?.weekly_trend || [];
    const languages = languageData || [];
    const closedLost = recapData?.closed_lost || [];

    // Funnel Summary rows (at-need QC, at-need ON, pre-need QC, pre-need ON,
    // totals). Each row carries its segment, which the row chart reuses.
    const funnelRows = buildFunnelRows(
        {
            qualifiedByRegion, wonByRegion, lostByRegion, pendingByRegion, prevPendingByRegion,
            prevQualifiedByRegion, prevWonByRegion, funnelByLanguage, prevFunnelByLanguage,
            wonTotalRows, prevWonTotalRows,
        },
        pageFilters,
        isCurrentMonth ? todayDay : undefined,
    );

    // Weekly trends: Calls Answered / Missed are call-volume (no timeline) and
    // stay full-month from recap.weekly_trend. Qualified / Won / Pending come from
    // the timeline-aware weekly RPC and respect the global timeline filter.
    const filteredWeekly = (() => {
        const byWeek: Record<string, any> = {};
        const ensure = (wk: string) => {
            if (!byWeek[wk]) byWeek[wk] = { week_start: wk, calls_answered: 0, missed: 0, qualified: 0, won: 0, pending: 0 };
            return byWeek[wk];
        };
        // Call columns: full-month, aggregated across deal types.
        (weeklyTrends || []).forEach((item: any) => {
            const row = ensure(item.week_start);
            row.calls_answered += Number(item.calls_answered) || 0;
            row.missed += Number(item.missed) || 0;
        });
        // Funnel columns: timeline-scoped.
        (weeklyFunnelByTimeline || []).filter(rowOk).forEach((item) => {
            const row = ensure(item.week_start);
            row.qualified += Number(item.qualified) || 0;
            row.won += Number(item.won) || 0;
            row.pending += Number(item.pending) || 0;
        });
        return Object.values(byWeek).sort((a: any, b: any) => a.week_start.localeCompare(b.week_start));
    })();

    // Agent performance — merge timeline-aware qualified/won/lost
    // (get_agent_detail_by_timeline) with timeline-aware pending/won_total
    // (get_agent_pending_won_total). Both are filtered by the global timeline
    // filter, then summed per (agent, deal_type, region, language). Built for the
    // current and previous month so the tables can show month-over-month deltas.
    const mergeAgentRows = (detailArr: any[], extraArr: any[]) => {
        const keyOf = (r: any) => `${r.agent_name}|${r.deal_type}|${r.region}|${r.language}`;
        const extraMap: Record<string, { pending: number; won_total: number }> = {};
        extraArr.forEach((r: any) => {
            const k = keyOf(r);
            if (!extraMap[k]) extraMap[k] = { pending: 0, won_total: 0 };
            extraMap[k].pending += Number(r.pending) || 0;
            extraMap[k].won_total += Number(r.won_total) || 0;
        });
        const detailMap: Record<string, { qualified: number; won: number; lost: number }> = {};
        detailArr.forEach((r: any) => {
            const k = keyOf(r);
            if (!detailMap[k]) detailMap[k] = { qualified: 0, won: 0, lost: 0 };
            detailMap[k].qualified += Number(r.qualified) || 0;
            detailMap[k].won += Number(r.won) || 0;
            detailMap[k].lost += Number(r.lost) || 0;
        });
        const allKeys = new Set<string>([...Object.keys(detailMap), ...Object.keys(extraMap)]);
        return Array.from(allKeys).map((k) => {
            const [agent_name, deal_type, region, language] = k.split('|');
            const d = detailMap[k] || { qualified: 0, won: 0, lost: 0 };
            const e = extraMap[k] || { pending: 0, won_total: 0 };
            return {
                agent_name,
                deal_type,
                region,
                language,
                qualified: d.qualified,
                won: d.won,
                lost: d.lost,
                pending: e.pending,
                won_total: e.won_total,
            };
        });
    };

    const agentMergedRows = mergeAgentRows(
        (agentDetailByTimeline || []).filter(rowOk),
        (agentPendingWonTotal || []).filter(rowOk),
    );
    const prevAgentMergedRows = mergeAgentRows(
        (prevAgentDetailByTimeline || []).filter(rowOk),
        (prevAgentPendingWonTotal || []).filter(rowOk),
    );

    const buildAgentTable = (dealType: string) => {
        const rows = agentMergedRows.filter((r: any) => r.deal_type === dealType);
        const prevRows = prevAgentMergedRows.filter((r: any) => r.deal_type === dealType);
        const agentTotals: Record<string, { qualified: number; won: number; lost: number; pending: number; won_total: number }> = {};
        rows.forEach((r: any) => {
            const a = r.agent_name;
            if (!agentTotals[a]) agentTotals[a] = { qualified: 0, won: 0, lost: 0, pending: 0, won_total: 0 };
            agentTotals[a].qualified += r.qualified;
            agentTotals[a].won += r.won;
            agentTotals[a].lost += r.lost;
            agentTotals[a].pending += r.pending;
            agentTotals[a].won_total += r.won_total;
        });
        const sortedAgents = Object.keys(agentTotals).sort((a, b) => agentTotals[b].qualified - agentTotals[a].qualified);

        // Build one region segment with its previous-month figures attached.
        const seg = (label: string, curRows: any[], prevSegRows: any[], predicate: (r: any) => boolean) => {
            const c = sumRows(curRows.filter(predicate));
            const p = sumRows(prevSegRows.filter(predicate));
            return {
                label, ...c,
                prevQualified: p.qualified, prevPending: p.pending, prevWon: p.won, prevWonTotal: p.won_total,
                winPct: c.qualified > 0 ? (c.won / c.qualified) * 100 : null,
                prevWinPct: p.qualified > 0 ? (p.won / p.qualified) * 100 : null,
            };
        };

        const result: any[] = [];
        sortedAgents.forEach((agent) => {
            const agentRows = rows.filter((r: any) => r.agent_name === agent);
            const prevAgentRows = prevRows.filter((r: any) => r.agent_name === agent);
            const subRows = [
                seg('QC French', agentRows, prevAgentRows, (r) => r.region === 'quebec' && normalizeLang(r.language) === 'French'),
                seg('QC English', agentRows, prevAgentRows, (r) => r.region === 'quebec' && normalizeLang(r.language) === 'English'),
                seg('Ontario', agentRows, prevAgentRows, (r) => r.region === 'ontario'),
            ].filter(r => r.qualified > 0 || r.won > 0 || r.lost > 0 || r.pending > 0 || r.won_total > 0
                || r.prevQualified > 0 || r.prevWon > 0 || r.prevWonTotal > 0 || r.prevPending > 0);

            const tot = agentTotals[agent];
            const pTot = sumRows(prevAgentRows);
            result.push({
                agent,
                qualified: tot.qualified,
                won: tot.won,
                lost: tot.lost,
                pending: tot.pending,
                won_total: tot.won_total,
                prevQualified: pTot.qualified,
                prevPending: pTot.pending,
                prevWon: pTot.won,
                prevWonTotal: pTot.won_total,
                winPct: tot.qualified > 0 ? (tot.won / tot.qualified) * 100 : null,
                prevWinPct: pTot.qualified > 0 ? (pTot.won / pTot.qualified) * 100 : null,
                rate: tot.qualified > 0 ? ((tot.won / tot.qualified) * 100).toFixed(0) : '-',
                subRows,
            });
        });
        return result;
    };

    const sumRows = (rows: any[]) => ({
        qualified: rows.reduce((s: number, r: any) => s + (Number(r.qualified) || 0), 0),
        won: rows.reduce((s: number, r: any) => s + (Number(r.won) || 0), 0),
        lost: rows.reduce((s: number, r: any) => s + (Number(r.lost) || 0), 0),
        pending: rows.reduce((s: number, r: any) => s + (Number(r.pending) || 0), 0),
        won_total: rows.reduce((s: number, r: any) => s + (Number(r.won_total) || 0), 0),
    });

    const atNeedAgents = buildAgentTable('at_need');
    const preNeedAgents = buildAgentTable('pre_need');

    // Language breakdown — normalize and aggregate (full-month; not timeline-scoped).
    const filteredLanguages = (() => {
        const byLang: Record<string, { language: string; qualified: number; won: number; lost: number; pending: number }> = {};
        (languages as any[]).forEach((l: any) => {
            const norm = normalizeLang(l.language);
            if (!byLang[norm]) byLang[norm] = { language: norm, qualified: 0, won: 0, lost: 0, pending: 0 };
            byLang[norm].qualified += Number(l.qualified) || 0;
            byLang[norm].won += Number(l.won) || 0;
            byLang[norm].lost += Number(l.lost) || 0;
        });
        // Pending by language — sum all pendingByRegion rows.
        (pendingByRegion || []).forEach((p) => {
            const norm = normalizeLang(p.language);
            if (!byLang[norm]) byLang[norm] = { language: norm, qualified: 0, won: 0, lost: 0, pending: 0 };
            byLang[norm].pending += Number(p.pending) || 0;
        });
        return Object.values(byLang).map((l) => ({
            ...l,
            win_pct:
                l.qualified > 0
                    ? ((l.won / l.qualified) * 100).toFixed(0)
                    : '-',
        }));
    })();

    // Closed lost — aggregate by reason, normalize labels (full-month; not timeline-scoped).
    const filteredClosedLost = (() => {
        const byReason: Record<string, number> = {};
        (closedLost as any[]).forEach((item: any) => {
            const reason = (item.reason || 'unspecified')
                .replace('no_reply', 'No Reply')
                .replace('unspecified', 'Unspecified')
                .replace('price', 'Price')
                .replace('traditional', 'Wanted Traditional')
                .replace('competitor', 'Chose Competitor')
                .replace('other', 'Other');
            byReason[reason] = (byReason[reason] || 0) + (Number(item.count) || 0);
        });
        const total = Object.values(byReason).reduce((s, c) => s + c, 0);
        return Object.entries(byReason)
            .map(([reason, count]) => ({
                reason,
                count,
                pct: total > 0 ? ((count / total) * 100).toFixed(0) : '0',
            }))
            .sort((a, b) => b.count - a.count);
    })();

    // Compact payload used for the AI exec summary. Keeps the prompt focused
    // and predictable rather than dumping raw RPC output.
    const buildExecSummaryPayload = () => {
        const currentForms = (Number(callMetrics.form_submissions_at_need) || 0)
            + (Number(callMetrics.form_submissions_pre_need) || 0);
        const prevCallMetrics = prevMonthRecap?.call_metrics || {};
        const prevForms = (Number(prevCallMetrics.form_submissions_at_need) || 0)
            + (Number(prevCallMetrics.form_submissions_pre_need) || 0);

        const grand = funnelRows.find((r) => r.label === 'Grand Total');
        const segmentRows = funnelRows
            .filter((r) => !r.isTotal)
            .map((r) => ({
                segment: r.label.trim(),
                qualified: r.qualified,
                pending: r.pending,
                won: r.won,
                lost: r.lost,
                conv_pct: r.rate,
                prev_qualified: r.prevQ,
                prev_won: r.prevW,
            }));

        const topAgents = [...atNeedAgents, ...preNeedAgents]
            .sort((a: any, b: any) => b.qualified - a.qualified)
            .slice(0, 5)
            .map((a: any) => ({
                agent: a.agent,
                qualified: a.qualified,
                won: a.won,
                lost: a.lost,
                conv_pct: a.rate,
            }));

        return {
            totals: {
                qualified: grand?.qualified ?? 0,
                pending: grand?.pending ?? 0,
                won: grand?.won ?? 0,
                won_total: grand?.wonTotal ?? 0,
                lost: grand?.lost ?? 0,
                conv_pct: grand?.rate ?? '-',
                prev_qualified: grand?.prevQ ?? 0,
                prev_won: grand?.prevW ?? 0,
                prev_won_total: grand?.prevWonTotal ?? 0,
            },
            call_metrics: {
                inbound: callMetrics.total_inbound_calls ?? 0,
                meaningful: callMetrics.meaningful_calls ?? 0,
                forms: currentForms,
                avg_answer_seconds: callMetrics.avg_answer_time_seconds ?? null,
                avg_followup_hours: callMetrics.avg_followup_hours ?? null,
                prev_inbound: prevCallMetrics.total_inbound_calls ?? 0,
                prev_forms: prevForms,
            },
            segments: segmentRows,
            lead_source: (leadSourceData || []).map((r) => ({
                channel: r.source_channel,
                qualified: Number(r.qualified) || 0,
                pending: Number(r.pending) || 0,
                won: Number(r.won) || 0,
                lost: Number(r.lost) || 0,
            })),
            top_agents: topAgents,
            closed_lost_reasons: filteredClosedLost.slice(0, 5),
        };
    };

    return (
        <Box sx={{ p: 2 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Monthly Recap — {formatMonthLabel(selectedMonth)}</Typography>
                <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBack}>
                    Back
                </Button>
            </Box>

            {/* Executive Summary */}
            <Card sx={{ mb: 2, borderLeft: '4px solid #1565c0' }}>
                <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <AutoAwesomeIcon fontSize="small" color="primary" />
                        <Typography variant="h6" sx={{ flexGrow: 1 }}>
                            Executive Summary
                        </Typography>
                        {execSummary && !loadingExecSummary && (
                            <IconButton
                                size="small"
                                onClick={() => void handleGenerateExecSummary(buildExecSummaryPayload())}
                                title="Regenerate"
                            >
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Stack>
                    {!execSummary && !loadingExecSummary && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                                Generate an AI-written headline for the meeting based on this month's metrics.
                            </Typography>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<AutoAwesomeIcon />}
                                onClick={() => void handleGenerateExecSummary(buildExecSummaryPayload())}
                            >
                                Generate
                            </Button>
                        </Box>
                    )}
                    {loadingExecSummary && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}
                    {execSummary && (
                        <Typography
                            variant="body1"
                            sx={{
                                whiteSpace: 'pre-wrap',
                                '& strong': { fontWeight: 700 },
                                lineHeight: 1.7,
                            }}
                            dangerouslySetInnerHTML={{
                                __html: execSummary
                                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                    .replace(/^- /gm, '&bull; '),
                            }}
                        />
                    )}
                </CardContent>
            </Card>

            {/* Timeline filter — page-wide. Tracks the React-Admin AppBar: sits just
                below it (48px) when visible, slides flush to the top when the AppBar
                hides on scroll-down, so it's always visible and moves with the header.
                Values must match quotes.timeline exactly. */}
            <Box sx={{ position: 'sticky', top: headerHidden ? 0 : 48, transition: 'top 225ms cubic-bezier(0.4, 0, 0.6, 1)', zIndex: 1100, bgcolor: 'background.default', py: 1.5, mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <Typography variant="caption" color="text.secondary">Timeline</Typography>
                    <ToggleButtonGroup
                        size="small"
                        value={timelineFilter}
                        exclusive
                        onChange={(_, v) => v && setTimelineFilter(v)}
                    >
                        <ToggleButton value="all">All</ToggleButton>
                        <ToggleButton value="Passed">Passed</ToggleButton>
                        <ToggleButton value="Imminent (1-7 days)">Imminent</ToggleButton>
                        <ToggleButton value="Near future (7-30 days)">Near future</ToggleButton>
                        <ToggleButton value="Preplanning (30+ days)">Preplanning</ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="caption" color="text.secondary">Source</Typography>
                    <ToggleButtonGroup
                        size="small"
                        value={sourceFilter}
                        exclusive
                        onChange={(_, v) => v && setSourceFilter(v)}
                    >
                        <ToggleButton value="all">All</ToggleButton>
                        <ToggleButton value="organic">Organic</ToggleButton>
                        <ToggleButton value="ad">Ads</ToggleButton>
                        <ToggleButton value="maps">Google Maps</ToggleButton>
                    </ToggleButtonGroup>
                    {(timelineFilter !== 'all' || sourceFilter !== 'all') && (
                        <Typography variant="caption" color="text.secondary">
                            {timelineFilter !== 'all' && 'Excludes leads with no timeline. '}
                            Call/form-volume metrics stay full-month.
                        </Typography>
                    )}
                </Stack>
            </Box>

            {/* Overview Table */}
            {(() => {
                // Qualified / Won are lead-funnel metrics, sourced from the
                // timeline-aware by-region RPCs so they respect the global filter
                // (and stay consistent with the Funnel Summary).
                const sumQ = (rows: any[] | undefined, dt: string) =>
                    (rows || []).filter((r: any) => r.deal_type === dt && rowOk(r))
                        .reduce((s: number, r: any) => s + (Number(r.qualified) || 0), 0);
                const sumW = (rows: any[] | undefined, dt: string) =>
                    (rows || []).filter((r: any) => r.deal_type === dt && rowOk(r))
                        .reduce((s: number, r: any) => s + (Number(r.won) || 0), 0);
                const atNeedQ = sumQ(qualifiedByRegion, 'at_need');
                const preNeedQ = sumQ(qualifiedByRegion, 'pre_need');
                const atNeedW = sumW(wonByRegion, 'at_need');
                const preNeedW = sumW(wonByRegion, 'pre_need');
                const totalForms = (Number(callMetrics.form_submissions_at_need) || 0) + (Number(callMetrics.form_submissions_pre_need) || 0);

                // Previous month values
                const prevCallMetrics = prevMonthRecap?.call_metrics || {};
                const prevAtNeedQ = sumQ(prevQualifiedByRegion, 'at_need');
                const prevPreNeedQ = sumQ(prevQualifiedByRegion, 'pre_need');
                const prevAtNeedW = sumW(prevWonByRegion, 'at_need');
                const prevPreNeedW = sumW(prevWonByRegion, 'pre_need');
                const prevTotalForms = (Number(prevCallMetrics.form_submissions_at_need) || 0) + (Number(prevCallMetrics.form_submissions_pre_need) || 0);

                return (
                    <TableContainer component={Paper} sx={{ mt: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell align="right">Total</TableCell>
                                    <TableCell align="right">At-Need</TableCell>
                                    <TableCell align="right">Pre-Need</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell>Inbound Calls</TableCell>
                                    <TableCell align="right"><ValueWithDelta value={Number(callMetrics.total_inbound_calls) || 0} prev={Number(prevCallMetrics.total_inbound_calls)} bold /></TableCell>
                                    <TableCell align="right" colSpan={2}>
                                        <Typography variant="caption" color="text.secondary">{callMetrics.meaningful_calls ?? 0} over 3 min</Typography>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Forms</TableCell>
                                    <TableCell align="right"><ValueWithDelta value={totalForms} prev={prevTotalForms} bold /></TableCell>
                                    <TableCell align="right">{callMetrics.form_submissions_at_need ?? 0}</TableCell>
                                    <TableCell align="right">{callMetrics.form_submissions_pre_need ?? 0}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Qualified</TableCell>
                                    <TableCell align="right"><ValueWithDelta value={atNeedQ + preNeedQ} prev={prevAtNeedQ + prevPreNeedQ} bold /></TableCell>
                                    <TableCell align="right"><ValueWithDelta value={atNeedQ} prev={prevAtNeedQ} /></TableCell>
                                    <TableCell align="right"><ValueWithDelta value={preNeedQ} prev={prevPreNeedQ} /></TableCell>
                                </TableRow>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Won</TableCell>
                                    <TableCell align="right"><ValueWithDelta value={atNeedW + preNeedW} prev={prevAtNeedW + prevPreNeedW} bold /></TableCell>
                                    <TableCell align="right"><ValueWithDelta value={atNeedW} prev={prevAtNeedW} bold /></TableCell>
                                    <TableCell align="right"><ValueWithDelta value={preNeedW} prev={prevPreNeedW} bold /></TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                        {timelineFilter !== 'all' && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1 }}>
                                Inbound Calls and Forms are not timeline-scoped (full month). Qualified and Won reflect the selected timeline.
                            </Typography>
                        )}
                    </TableContainer>
                );
            })()}

            {/* Funnel Summary Table */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                Funnel Summary
            </Typography>
            {timelineFilter !== 'all' && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Excludes leads with no timeline. Won Total ignores the Timeline filter (a signed contract carries no timeline); it does follow Source.
                </Typography>
            )}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell align="right">Qualified</TableCell>
                            <TableCell align="right" title="Form Filled: has funeral, no contract signed yet">Pending</TableCell>
                            <TableCell align="right">Won</TableCell>
                            <TableCell align="right" title="Funerals with a signed contract in the window. Follows the Source filter through the lead behind the funeral; a contract carries no timeline, so it ignores the Timeline filter.">Won Total</TableCell>
                            <TableCell align="right">Lost</TableCell>
                            <TableCell align="right">Open</TableCell>
                            <TableCell align="right">Conv%</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {funnelRows.map((row, i) => (
                            <TableRow
                                key={i}
                                sx={{
                                    ...(row.isTotal ? { fontWeight: 'bold', bgcolor: '#f5f5f5' } : {}),
                                    cursor: 'pointer', '&:hover': { bgcolor: '#e3f2fd' },
                                }}
                                onClick={() => { setShowWon(false); setChartRow({ label: row.label, segment: row.segment }); }}
                            >
                                <TableCell sx={row.isTotal ? { fontWeight: 700 } : {}}>{row.label}</TableCell>
                                <TableCell align="right"><ValueWithDelta value={row.qualified} prev={row.prevQ} bold={row.isTotal} /></TableCell>
                                <TableCell align="right"><ValueWithDelta value={row.pending} prev={row.prevP} bold={row.isTotal} /></TableCell>
                                <TableCell align="right"><ValueWithDelta value={row.won} prev={row.prevW} bold={row.isTotal} /></TableCell>
                                <TableCell align="right"><ValueWithDelta value={row.wonTotal} prev={row.prevWonTotal} bold={row.isTotal} /></TableCell>
                                <TableCell align="right" sx={row.isTotal ? { fontWeight: 700 } : {}}>{row.lost}</TableCell>
                                <TableCell align="right" sx={row.isTotal ? { fontWeight: 700 } : {}}>{row.open}</TableCell>
                                <TableCell align="right" sx={row.isTotal ? { fontWeight: 700 } : {}}>{row.rate}%</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Lead Source */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                Lead Source
            </Typography>
            {(() => {
                // Aggregate per channel, applying the global timeline filter to the
                // timeline-keyed rows (current + previous month).
                type SourceAgg = { qualified: number; pending: number; won: number; lost: number };
                const aggByChannel = (rows: typeof leadSourceData) => {
                    const m: Record<string, SourceAgg> = {};
                    (rows || []).filter(rowOk).forEach((r) => {
                        const ch = r.source_channel || 'unknown';
                        if (!m[ch]) m[ch] = { qualified: 0, pending: 0, won: 0, lost: 0 };
                        m[ch].qualified += Number(r.qualified) || 0;
                        m[ch].pending += Number(r.pending) || 0;
                        m[ch].won += Number(r.won) || 0;
                        m[ch].lost += Number(r.lost) || 0;
                    });
                    return m;
                };
                const curr = aggByChannel(leadSourceData);
                const prevAgg = aggByChannel(prevLeadSourceData);
                const sources = Object.entries(curr)
                    .map(([source_channel, c]) => {
                        const prev = prevAgg[source_channel];
                        const rate = c.qualified > 0 ? ((c.won / c.qualified) * 100).toFixed(0) : '-';
                        return {
                            source_channel,
                            qualified: c.qualified,
                            pending: c.pending,
                            won: c.won,
                            lost: c.lost,
                            rate,
                            prevQualified: prev ? prev.qualified : null,
                            prevPending: prev ? prev.pending : null,
                            prevWon: prev ? prev.won : null,
                        };
                    })
                    .sort((a, b) => b.qualified - a.qualified);
                return (
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Channel</TableCell>
                                    <TableCell align="right">Qualified</TableCell>
                                    <TableCell align="right" title="Form Filled: has funeral, no contract signed yet">Pending</TableCell>
                                    <TableCell align="right">Won</TableCell>
                                    <TableCell align="right">Lost</TableCell>
                                    <TableCell align="right">Conv%</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sources.map((row) => (
                                    <TableRow key={row.source_channel}>
                                        <TableCell>{channelLabel(row.source_channel)}</TableCell>
                                        <TableCell align="right"><ValueWithDelta value={row.qualified} prev={row.prevQualified} /></TableCell>
                                        <TableCell align="right"><ValueWithDelta value={row.pending} prev={row.prevPending} /></TableCell>
                                        <TableCell align="right"><ValueWithDelta value={row.won} prev={row.prevWon} /></TableCell>
                                        <TableCell align="right">{row.lost}</TableCell>
                                        <TableCell align="right">{row.rate}{row.rate !== '-' ? '%' : ''}</TableCell>
                                    </TableRow>
                                ))}
                                {sources.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">No data</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                );
            })()}

            {/* Additional Metrics Cards */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                Additional Metrics
            </Typography>
            <Grid container spacing={2}>
                {[
                    { label: 'Avg Answer Time', value: callMetrics.avg_answer_time_seconds != null ? `${callMetrics.avg_answer_time_seconds}s` : '-' },
                    { label: 'Avg Follow-up Speed', value: callMetrics.avg_followup_hours != null ? `${callMetrics.avg_followup_hours}h` : '-' },
                    { label: 'After-Hours (Wkday)', value: callMetrics.after_hours_calls ?? '-' },
                    { label: 'Weekend Calls', value: callMetrics.weekend_calls ?? '-' },
                    { label: 'Repeat Callers', value: callMetrics.repeat_callers ?? '-' },
                ].map((metric) => (
                    <Grid item xs={4} md key={metric.label}>
                        <Card sx={{ bgcolor: '#f5f5f5', height: '100%' }}>
                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    {metric.label}
                                </Typography>
                                <Typography variant="h5">{metric.value}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Weekly Trend Table */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                Weekly Trends
            </Typography>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Week</TableCell>
                            <TableCell align="right">Calls Answered</TableCell>
                            <TableCell align="right">Missed</TableCell>
                            <TableCell align="right">Qualified</TableCell>
                            <TableCell align="right" title="Form Filled: has funeral, no contract signed yet">Pending</TableCell>
                            <TableCell align="right">Won</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredWeekly.map((row: any, i: number) => (
                            <TableRow key={i}>
                                <TableCell>{row.week_start}</TableCell>
                                <TableCell align="right">{row.calls_answered ?? 0}</TableCell>
                                <TableCell align="right">{row.missed ?? 0}</TableCell>
                                <TableCell align="right">{row.qualified ?? 0}</TableCell>
                                <TableCell align="right">{row.pending ?? 0}</TableCell>
                                <TableCell align="right">{row.won ?? 0}</TableCell>
                            </TableRow>
                        ))}
                        {filteredWeekly.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">No data</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                {timelineFilter !== 'all' && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1 }}>
                        Calls Answered and Missed are not timeline-scoped (full month).
                    </Typography>
                )}
            </TableContainer>

            {/* Day of Week */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                Day of Week
            </Typography>
            {(() => {
                // Qualified overlays the timeline-aware RPC (matched by isodow);
                // inbound/outbound call columns stay full-month from the recap.
                const qualByDow: Record<number, number> = {};
                (dayOfWeekQualByTimeline || []).filter(rowOk).forEach((r) => {
                    qualByDow[r.isodow] = (qualByDow[r.isodow] || 0) + (Number(r.qualified) || 0);
                });
                return (
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Day</TableCell>
                                    <TableCell align="right">Inbound Answered</TableCell>
                                    <TableCell align="right">Outbound</TableCell>
                                    <TableCell align="right">Qualified</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(recapData?.day_of_week || []).map((row: any, i: number) => (
                                    <TableRow key={i}>
                                        <TableCell>{row.day_name}</TableCell>
                                        <TableCell align="right">{row.inbound_answered ?? 0}</TableCell>
                                        <TableCell align="right">{row.outbound ?? 0}</TableCell>
                                        <TableCell align="right">{qualByDow[Number(row.isodow)] ?? 0}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {timelineFilter !== 'all' && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1 }}>
                                Inbound Answered and Outbound are not timeline-scoped (full month).
                            </Typography>
                        )}
                    </TableContainer>
                );
            })()}

            {/* Agent Performance — At-Need */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                Agent Performance — At-Need
            </Typography>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Agent</TableCell>
                            <TableCell>Segment</TableCell>
                            <TableCell align="right">Qualified</TableCell>
                            <TableCell align="right" title="Form Filled: has funeral, no contract signed yet">Pending</TableCell>
                            <TableCell align="right">Won</TableCell>
                            <TableCell align="right">Lost</TableCell>
                            <TableCell align="right" title="Funerals signed in window attributed via first-call agent (qualified anytime)">Won Total</TableCell>
                            <TableCell align="right">Win%</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {atNeedAgents.map((agent: any) => (
                            <>
                                {agent.subRows.map((sub: any, si: number) => (
                                    <TableRow key={`${agent.agent}-${si}`}>
                                        {si === 0 && (
                                            <TableCell rowSpan={agent.subRows.length + 1} sx={{ fontWeight: 700, verticalAlign: 'top' }}>
                                                {agent.agent}
                                            </TableCell>
                                        )}
                                        <TableCell sx={{ pl: 2, color: 'text.secondary' }}>{sub.label}</TableCell>
                                        <TableCell align="right"><ValueWithDelta value={sub.qualified} prev={sub.prevQualified} /></TableCell>
                                        <TableCell align="right"><ValueWithDelta value={sub.pending} prev={sub.prevPending} /></TableCell>
                                        <TableCell align="right"><ValueWithDelta value={sub.won} prev={sub.prevWon} /></TableCell>
                                        <TableCell align="right">{sub.lost}</TableCell>
                                        <TableCell align="right"><ValueWithDelta value={sub.won_total} prev={sub.prevWonTotal} /></TableCell>
                                        <TableCell align="right"><RateWithDelta rate={sub.winPct} prevRate={sub.prevWinPct} /></TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}><ValueWithDelta value={agent.qualified} prev={agent.prevQualified} bold /></TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}><ValueWithDelta value={agent.pending} prev={agent.prevPending} bold /></TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}><ValueWithDelta value={agent.won} prev={agent.prevWon} bold /></TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{agent.lost}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}><ValueWithDelta value={agent.won_total} prev={agent.prevWonTotal} bold /></TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}><RateWithDelta rate={agent.winPct} prevRate={agent.prevWinPct} bold /></TableCell>
                                </TableRow>
                            </>
                        ))}
                        {atNeedAgents.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} align="center">No data</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Agent Performance — Pre-Need */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                Agent Performance — Pre-Need
            </Typography>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Agent</TableCell>
                            <TableCell>Segment</TableCell>
                            <TableCell align="right">Qualified</TableCell>
                            <TableCell align="right" title="Form Filled: has funeral, no contract signed yet">Pending</TableCell>
                            <TableCell align="right">Won</TableCell>
                            <TableCell align="right">Lost</TableCell>
                            <TableCell align="right" title="Funerals signed in window attributed via first-call agent (qualified anytime)">Won Total</TableCell>
                            <TableCell align="right">Win%</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {preNeedAgents.map((agent: any) => (
                            <>
                                {agent.subRows.map((sub: any, si: number) => (
                                    <TableRow key={`${agent.agent}-pn-${si}`}>
                                        {si === 0 && (
                                            <TableCell rowSpan={agent.subRows.length + 1} sx={{ fontWeight: 700, verticalAlign: 'top' }}>
                                                {agent.agent}
                                            </TableCell>
                                        )}
                                        <TableCell sx={{ pl: 2, color: 'text.secondary' }}>{sub.label}</TableCell>
                                        <TableCell align="right"><ValueWithDelta value={sub.qualified} prev={sub.prevQualified} /></TableCell>
                                        <TableCell align="right"><ValueWithDelta value={sub.pending} prev={sub.prevPending} /></TableCell>
                                        <TableCell align="right"><ValueWithDelta value={sub.won} prev={sub.prevWon} /></TableCell>
                                        <TableCell align="right">{sub.lost}</TableCell>
                                        <TableCell align="right"><ValueWithDelta value={sub.won_total} prev={sub.prevWonTotal} /></TableCell>
                                        <TableCell align="right"><RateWithDelta rate={sub.winPct} prevRate={sub.prevWinPct} /></TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}><ValueWithDelta value={agent.qualified} prev={agent.prevQualified} bold /></TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}><ValueWithDelta value={agent.pending} prev={agent.prevPending} bold /></TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}><ValueWithDelta value={agent.won} prev={agent.prevWon} bold /></TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{agent.lost}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}><ValueWithDelta value={agent.won_total} prev={agent.prevWonTotal} bold /></TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}><RateWithDelta rate={agent.winPct} prevRate={agent.prevWinPct} bold /></TableCell>
                                </TableRow>
                            </>
                        ))}
                        {preNeedAgents.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} align="center">No data</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Language Breakdown Table */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                Language Breakdown
            </Typography>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Language</TableCell>
                            <TableCell align="right">Qualified</TableCell>
                            <TableCell align="right" title="Form Filled: has funeral, no contract signed yet">Pending</TableCell>
                            <TableCell align="right">Won</TableCell>
                            <TableCell align="right">Lost</TableCell>
                            <TableCell align="right">Win%</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredLanguages.map((row: any, i: number) => (
                            <TableRow key={i}>
                                <TableCell>{row.language}</TableCell>
                                <TableCell align="right">{row.qualified ?? 0}</TableCell>
                                <TableCell align="right">{row.pending ?? 0}</TableCell>
                                <TableCell align="right">{row.won ?? 0}</TableCell>
                                <TableCell align="right">{row.lost ?? 0}</TableCell>
                                <TableCell align="right">{row.win_pct}%</TableCell>
                            </TableRow>
                        ))}
                        {filteredLanguages.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">No data</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Closed Lost Analysis Table */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                Closed Lost Analysis
            </Typography>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Reason</TableCell>
                            <TableCell align="right">Count</TableCell>
                            <TableCell align="right">% of Total</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredClosedLost.map((row: any, i: number) => (
                            <TableRow key={i}>
                                <TableCell>{row.reason || '-'}</TableCell>
                                <TableCell align="right">{row.count ?? 0}</TableCell>
                                <TableCell align="right">{row.pct}%</TableCell>
                            </TableRow>
                        ))}
                        {filteredClosedLost.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3} align="center">No data</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Top 5 Lost Cases Review */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                Lost Case Review
            </Typography>
            {lostCases.length === 0 && !loadingRecap && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Generate an AI-powered review of the top lost cases this month.
                    </Typography>
                    <Button variant="contained" onClick={handleGenerateRecap}>
                        Generate Recap
                    </Button>
                </Box>
            )}
            {loadingRecap && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            )}
            {lostCases.length > 0 &&
                lostCases.map((c: any, index: number) => (
                    <Card key={c.lead_id || index} sx={{ mb: 3 }}>
                        <CardContent>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                    {c.contact_name || 'Unknown'}
                                </Typography>
                                {c.region && <Chip label={c.region} size="small" />}
                                {c.timeline && <Chip label={c.timeline} size="small" variant="outlined" />}
                                {c.closed_lost_reason && (
                                    <Chip label={c.closed_lost_reason} size="small" color="error" variant="outlined" />
                                )}
                                {c.language && <Chip label={normalizeLang(c.language)} size="small" variant="outlined" />}
                                <IconButton
                                    size="small"
                                    onClick={() => void handleReplaceCase(index)}
                                    title="Replace with another case"
                                    disabled={replacingIndex === index}
                                >
                                    {replacingIndex === index ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                                </IconButton>
                            </Stack>
                            {aiAnalyses[c.lead_id] ? (
                                <Typography
                                    variant="body1"
                                    sx={{
                                        whiteSpace: 'pre-wrap',
                                        '& strong': { fontWeight: 700 },
                                        lineHeight: 1.7,
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: aiAnalyses[c.lead_id]
                                            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                            .replace(/^- /gm, '&bull; ')
                                    }}
                                />
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Generating analysis...
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                ))}
            {/* Chart Dialog — the clicked Funnel Summary row, under the page filters */}
            <Dialog open={!!chartRow} onClose={() => setChartRow(null)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                        {chartRow?.label.trim()} — Daily Breakdown
                        <Typography variant="body2" color="text.secondary">
                            Timeline: {timelineFilter === 'all' ? 'All' : timelineFilter}
                            {' · '}
                            Source: {sourceFilter === 'all' ? 'All' : sourceFilter === 'maps' ? 'Google Maps' : channelLabel(sourceFilter)}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            Compare to
                        </Typography>
                        <Select
                            size="small"
                            value={compareMonth}
                            onChange={(e) => setCompareMonthOverride(e.target.value)}
                        >
                            {compareMonthOptions.map((m) => (
                                <MenuItem key={m} value={m}>{formatMonthLabel(m)}</MenuItem>
                            ))}
                        </Select>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    {chartRow && (() => {
                        // get_funnel_daily ships in a migration; until it is applied
                        // (or if the call fails) say so rather than spin forever.
                        if (funnelDailyFailed) {
                            return (
                                <Typography color="error" sx={{ py: 4 }}>
                                    Couldn&apos;t load the daily breakdown for this row.
                                </Typography>
                            );
                        }
                        if (!funnelDaily) {
                            return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
                        }

                        // Qualified and Won are the row's own counts split by day
                        // (get_funnel_daily), summed through the same segment and page
                        // filters as the row, so each line ends on the row's number.
                        //
                        // Won Total counts contracts signed this month. Under a filter it
                        // follows both filters, reading the stage and source off the
                        // case's quote, so a filtered chart never mixes a filtered series
                        // with an unfiltered one. Two caveats, both visible to the user in
                        // the caption: a case whose quote recorded no stage drops out (so
                        // the stages do not sum to the All view), and the stage is the one
                        // captured at quote time, which for most won cases is out of date
                        // by the time the contract is signed.
                        //
                        // Filtered: all three series. Unfiltered: Qualified and Won Total,
                        // with Won behind a toggle that defaults off.
                        const seg = chartRow.segment;
                        const filtered = timelineFilter !== 'all' || sourceFilter !== 'all';
                        const showWonSeries = filtered || showWon;
                        const currentDays = new Date(selYear, selMonthNum, 0).getDate();
                        const currentQ = dailyCumulative(funnelDaily, 'qualified', seg, pageFilters, selectedMonth, currentDays);
                        const currentWon = dailyCumulative(funnelDaily, 'won', seg, pageFilters, selectedMonth, currentDays);
                        const currentW = wonTotalCumulative(wonTotalRows, seg, pageFilters, selectedMonth, currentDays);

                        const prevDays = compareDaysInMonth;
                        const prevQ = dailyCumulative(compareFunnelDaily, 'qualified', seg, pageFilters, compareMonth, prevDays);
                        const prevWon = dailyCumulative(compareFunnelDaily, 'won', seg, pageFilters, compareMonth, prevDays);
                        const prevW = wonTotalCumulative(compareWonTotalRows, seg, pageFilters, compareMonth, prevDays);

                        // Cut the current-month series at today so the line doesn't
                        // stay flat for future days that are in the series but empty.
                        const currentCutoff = isCurrentMonth ? todayDay : currentDays;

                        // Hover tooltip order: won pairs first, then qualified.
                        const tooltipOrder = ['Won', 'Prev Won', 'Won Total', 'Prev Won Total', 'Qualified', 'Prev Qualified'];

                        // Merge into chart data using day-of-month (1, 2, 3...)
                        const maxDays = Math.max(currentDays, prevDays);
                        const chartData = Array.from({ length: maxDays }, (_, i) => ({
                            day: i + 1,
                            'Qualified': i < currentCutoff ? currentQ[i] ?? null : null,
                            'Won Total': i < currentCutoff ? currentW[i] ?? null : null,
                            'Prev Qualified': prevQ[i] ?? null,
                            'Prev Won Total': prevW[i] ?? null,
                            ...(showWonSeries ? {
                                'Won': i < currentCutoff ? currentWon[i] ?? null : null,
                                'Prev Won': prevWon[i] ?? null,
                            } : {}),
                        }));

                        return (
                            <>
                                {!filtered && (
                                    <FormControlLabel
                                        sx={{ ml: 0 }}
                                        control={<Switch size="small" checked={showWon} onChange={(e) => setShowWon(e.target.checked)} />}
                                        label={
                                            <Typography variant="caption" color="text.secondary">
                                                Show Won (contracts signed by leads qualified this month)
                                            </Typography>
                                        }
                                    />
                                )}
                                <Box sx={{ width: '100%', height: 400, mt: 2 }}>
                                    <ResponsiveContainer>
                                        <LineChart data={chartData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="day" fontSize={11} />
                                            <YAxis allowDecimals={false} />
                                            <Tooltip itemSorter={(item: any) => tooltipOrder.indexOf(String(item.dataKey))} />
                                            <Legend />
                                            {isCurrentMonth && (
                                                <ReferenceLine x={todayDay} stroke="#888" strokeDasharray="3 3" label={{ value: 'Today', position: 'top', fontSize: 11, fill: '#666' }} />
                                            )}
                                            <Line type="monotone" dataKey="Qualified" stroke="#2e7d32" strokeWidth={2.5} dot={false} />
                                            <Line type="monotone" dataKey="Won Total" stroke="#1565c0" strokeWidth={2.5} dot={false} />
                                            <Line type="monotone" dataKey="Prev Qualified" stroke="#2e7d32" strokeWidth={1} dot={false} strokeDasharray="5 5" strokeOpacity={0.4} />
                                            <Line type="monotone" dataKey="Prev Won Total" stroke="#1565c0" strokeWidth={1} dot={false} strokeDasharray="5 5" strokeOpacity={0.4} />
                                            {showWonSeries && (
                                                <Line type="monotone" dataKey="Won" stroke="#ef6c00" strokeWidth={2.5} dot={false} />
                                            )}
                                            {showWonSeries && (
                                                <Line type="monotone" dataKey="Prev Won" stroke="#ef6c00" strokeWidth={1} dot={false} strokeDasharray="5 5" strokeOpacity={0.4} />
                                            )}
                                        </LineChart>
                                    </ResponsiveContainer>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    {filtered
                                        ? 'Filtered view: Qualified, Won and Won Total all follow Timeline and Source. Won Total reads the stage and source from the case’s quote, so cases whose quote recorded no stage are left out and the stages do not add up to the All view. It is also the stage captured when the quote was made, which is usually out of date by the time the case is won, and it is filtered here while its table column is not.'
                                        : 'Unfiltered view: Qualified and Won Total (every contract signed this month). Turn on Won to add contracts signed by leads qualified this month.'}
                                </Typography>
                            </>
                        );
                    })()}
                </DialogContent>
            </Dialog>
        </Box>
    );
};
